import React, { useState, useCallback } from 'react';
import { cn } from '@/lib/core/utils';
import { ExternalLink, Undo2, CheckCircle, Loader2, RotateCcw, Pencil, Check, X } from 'lucide-react';
import { CollapseChevron } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { SimpleTooltip } from '@/components/overlays/simple-tooltip';
import { useDashboardTab } from '@/context/DashboardTabContext';
import { useAuth } from '@/context/AuthContext';
import { useNavigateToTab } from '@/lib/navigation';
import { BUSINESS_SECTION_ALIASES, EMPLOYEE_SECTION_ALIASES } from '@/lib/constants/dashboard';
import { AI_ASSISTANT } from '@/lib/ai-chat/constants';
import { MarkdownRenderer } from './MarkdownRenderer';
import { AIQuestionsForm } from './AIQuestionsForm';
import { useTypewriter } from '@/hooks/ai-chat/useTypewriter';
import type { AIMessageProps, NavigationHint, AIToolCall, AIQuestionAnswers, AIMessage as AIMessageType } from './types';

// Get initials from name or email
const getInitials = (fullName?: string | null, email?: string | null): string => {
  if (fullName) {
    return fullName
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  if (email) {
    return email[0].toUpperCase();
  }
  return 'U';
};

interface AIMessageComponentProps extends AIMessageProps {
  isLatest?: boolean;
  businessLogoUrl?: string | null;
  businessId?: string | null;
  onQuestionsSubmit?: (answers: AIQuestionAnswers, displayText: string) => void;
  onResend?: (message: AIMessageType) => void;
  onEdit?: (message: AIMessageType, newContent: string) => void;
  onCancelTools?: () => Promise<void>;
}

export const AIMessage: React.FC<AIMessageComponentProps> = ({
  message,
  onRevert,
  onConfirmTools,
  onCancelTools,
  isLatest = false,
  businessLogoUrl,
  businessId,
  onQuestionsSubmit,
  onResend,
  onEdit,
}) => {
  const [showToolCalls, setShowToolCalls] = useState(false);
  const [revertingToolIndex, setRevertingToolIndex] = useState<number | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [questionsSubmitted, setQuestionsSubmitted] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(message.content);
  const [showUserClicked, setShowUserClicked] = useState(false);
  const { user } = useAuth();
  const navigateToTab = useNavigateToTab();
  const {
    setHighlightedItem,
    setSelectedShiftDate,
    setNavigateToShiftMonth,
    setShiftToSelect
  } = useDashboardTab();
  const isUser = message.role === 'user';

  // Typewriter effect for latest AI message (only on first render)
  const { displayedText } = useTypewriter(message.content, {
    enabled: !isUser && isLatest,
  });

  const handleShowNavigation = (nav: NavigationHint) => {
    // For shifts, select for editing instead of just highlighting
    if (nav.resourceType === 'shift' && nav.resourceId) {
      setShiftToSelect(nav.resourceId);
    }
    // For other types, use highlight (employees, reviews)
    else if (nav.resourceId && (nav.resourceType === 'employee' || nav.resourceType === 'review')) {
      setHighlightedItem({ type: nav.resourceType, id: nav.resourceId });
    }

    // Navigate to date if shift - also navigate calendar to that month
    if (nav.resourceType === 'shift' && nav.date) {
      const shiftDate = new Date(nav.date);
      setSelectedShiftDate(shiftDate);
      setNavigateToShiftMonth(shiftDate); // Trigger calendar to navigate to that month
    }

    // Switch to correct dashboard and tab
    navigateToTab(nav.dashboard === 'employee' ? 'employee' : 'business', nav.tab);
  };

  const handleRevert = async (toolCall: AIToolCall, index: number) => {
    if (!onRevert) return;
    setRevertingToolIndex(index);
    try {
      await onRevert(toolCall);
    } finally {
      setRevertingToolIndex(null);
    }
  };

  const isCreateTool = (name: string) => name === 'create_shift' || name === 'create_employee';

  // Handle questions form submission
  const handleQuestionsSubmit = useCallback((answers: AIQuestionAnswers, displayText: string) => {
    setQuestionsSubmitted(true);
    onQuestionsSubmit?.(answers, displayText);
  }, [onQuestionsSubmit]);

  const handleConfirmTools = async () => {
    if (!onConfirmTools || !message.pendingTools) return;
    setIsConfirming(true);
    try {
      await onConfirmTools(message.pendingTools);
    } finally {
      setIsConfirming(false);
    }
  };

  const handleResend = () => {
    onResend?.(message);
  };

  const handleEditSave = () => {
    if (editContent.trim() && editContent !== message.content) {
      onEdit?.(message, editContent.trim());
    }
    setIsEditing(false);
  };

  const handleEditCancel = () => {
    setEditContent(message.content);
    setIsEditing(false);
  };

  const handleEditKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleEditSave();
    } else if (e.key === 'Escape') {
      handleEditCancel();
    }
  };

  const initials = getInitials(user?.user_metadata?.full_name, user?.email);
  const contentToShow = isUser ? message.content : (isLatest ? displayedText : message.content);

  return (
    <div
      className={cn(
        'flex gap-2 mb-3',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {/* Avatar */}
      {isUser ? (
        businessLogoUrl ? (
          <div className="flex-shrink-0 h-5 w-5 rounded-full bg-card flex items-center justify-center ring-1 ring-border">
            <img
              src={businessLogoUrl}
              alt="Business"
              className="h-3.5 w-3.5 rounded-sm"
            />
          </div>
        ) : (
          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-primary/30 to-primary/50 flex items-center justify-center ring-1 ring-border">
            <span className="text-xs font-semibold text-primary">{initials}</span>
          </div>
        )
      ) : (
        <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ring-1 ring-border">
          <img
            src="/media/tag-innovation-logo.png"
            alt={AI_ASSISTANT.name}
            className="h-4 w-4 rounded-sm"
          />
        </div>
      )}

      {/* Message Content */}
      <div className={cn('max-w-[80%]', isUser && 'flex flex-col items-end')}>
        <div
          className={cn(
            'rounded-lg px-3 py-2 text-sm',
            isUser
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-foreground'
          )}
        >
          {isUser ? (
            isEditing ? (
              <div className="space-y-2">
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  onKeyDown={handleEditKeyDown}
                  className="w-full min-h-[60px] p-2 rounded bg-primary-foreground text-primary text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary-foreground/50"
                  autoFocus
                />
                <div className="flex gap-1 justify-end">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-xs bg-primary-foreground/20 hover:bg-primary-foreground/30 text-primary-foreground"
                    onClick={handleEditCancel}
                  >
                    <X className="h-3 w-3 mr-1" />
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-xs bg-primary-foreground/20 hover:bg-primary-foreground/30 text-primary-foreground"
                    onClick={handleEditSave}
                    disabled={!editContent.trim() || editContent === message.content}
                  >
                    <Check className="h-3 w-3 mr-1" />
                    Save & Send
                  </Button>
                </div>
              </div>
            ) : (
              <p className="whitespace-pre-wrap break-words">{contentToShow}</p>
            )
          ) : (
            <MarkdownRenderer content={contentToShow} />
          )}

        {/* Tool Calls (only for assistant messages) */}
        {!isUser && message.toolCalls && message.toolCalls.length > 0 && (
          <div className="mt-2 pt-2 border-t border-border/50">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => setShowToolCalls(!showToolCalls)}
            >
              <CollapseChevron open={showToolCalls} className="mr-1 !h-3 !w-3" />
              {message.toolCalls.length} tool call{message.toolCalls.length > 1 ? 's' : ''}
            </Button>

            {showToolCalls && (
              <div className="mt-2 space-y-2">
                {message.toolCalls.map((tool, index) => (
                  <div
                    key={index}
                    className="text-xs bg-background/50 rounded p-2"
                  >
                    <div className="font-medium text-muted-foreground">
                      {tool.name}
                    </div>
                    {tool.error ? (
                      <div className="text-destructive mt-1">{tool.error}</div>
                    ) : tool.result ? (
                      <>
                        <pre className="mt-1 text-xs overflow-x-auto max-h-24 overflow-y-auto">
                          {typeof tool.result === 'string'
                            ? tool.result
                            : JSON.stringify(tool.result, null, 2)}
                        </pre>
                        {/* Action buttons for tool results */}
                        <div className="flex gap-1 mt-2">
                          {/* Show button - navigate to the relevant tab */}
                          {tool.result?.navigation && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-6 text-xs"
                              onClick={() => handleShowNavigation(tool.result!.navigation!)}
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              Show
                            </Button>
                          )}
                          {/* Revert button - only for create operations that succeeded and haven't been reverted */}
                          {isCreateTool(tool.name) &&
                           tool.result?.success &&
                           !tool.result?.reverted &&
                           onRevert && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 text-xs text-destructive hover:text-destructive"
                              onClick={() => handleRevert(tool, index)}
                              disabled={revertingToolIndex === index}
                            >
                              <Undo2 className="h-3 w-3 mr-1" />
                              {revertingToolIndex === index ? 'Reverting...' : 'Revert'}
                            </Button>
                          )}
                        </div>
                      </>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Pending Tools Confirmation */}
        {!isUser && message.pendingTools && message.pendingTools.length > 0 && onConfirmTools && (
          <div className="mt-3 p-2 bg-warning/10 border border-warning/30 rounded-md">
            <div className="text-xs font-medium text-warning mb-2">
              Actions requiring confirmation:
            </div>
            <ul className="text-xs space-y-1 mb-2">
              {message.pendingTools.map((tool, index) => (
                <li key={index} className="flex items-start gap-1">
                  <span className="text-warning">•</span>
                  <span>{tool.description}</span>
                </li>
              ))}
            </ul>
            <div className="flex gap-2">
              <Button
                size="sm"
                className="h-7 text-xs bg-success hover:bg-success/90"
                onClick={handleConfirmTools}
                disabled={isConfirming}
              >
                {isConfirming ? (
                  <>
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    Executing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Confirm
                  </>
                )}
              </Button>
              {onCancelTools && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs text-muted-foreground hover:text-foreground"
                  onClick={onCancelTools}
                  disabled={isConfirming}
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Structured Questions Form */}
        {!isUser && message.questions && message.questions.length > 0 && !questionsSubmitted && (
          <div className="mt-3 pt-2 border-t border-border/50">
            <AIQuestionsForm
              questions={message.questions}
              businessId={businessId ?? null}
              onSubmit={handleQuestionsSubmit}
            />
          </div>
        )}

        {/* Navigation Action Button (for show_user) */}
        {!isUser && message.showUser && !showUserClicked && (
          <div className="mt-3 pt-2 border-t border-border/50">
            <Button
              size="sm"
              className="h-8 text-sm"
              onClick={() => {
                if (message.showUser) {
                  setShowUserClicked(true);

                  const section = message.showUser.section?.toLowerCase();

                  if (section && EMPLOYEE_SECTION_ALIASES[section]) {
                    navigateToTab('employee', EMPLOYEE_SECTION_ALIASES[section]);

                    // Dispatch event for element highlighting after navigation
                    if (message.showUser.element) {
                      setTimeout(() => {
                        window.dispatchEvent(new CustomEvent('ai-show-user', {
                          detail: { element: message.showUser!.element, message: message.showUser!.message }
                        }));
                      }, 300);
                    }
                  } else if (section && BUSINESS_SECTION_ALIASES[section]) {
                    navigateToTab('business', BUSINESS_SECTION_ALIASES[section]);

                    // Dispatch event for element highlighting after navigation
                    if (message.showUser.element) {
                      setTimeout(() => {
                        window.dispatchEvent(new CustomEvent('ai-show-user', {
                          detail: { element: message.showUser!.element, message: message.showUser!.message }
                        }));
                      }, 300);
                    }
                  } else {
                    // No recognized section or just element - let ShowUserContext handle
                    // (e.g., landing page sections, pricing, etc.)
                    window.dispatchEvent(new CustomEvent('ai-show-user', {
                      detail: message.showUser
                    }));
                  }
                }
              }}
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              {message.showUser.message || `Go to ${message.showUser.section || 'page'}`}
            </Button>
          </div>
        )}

        {/* Timestamp */}
        <div
          className={cn(
            'text-2xs mt-1 opacity-70',
            isUser ? 'text-right' : 'text-left'
          )}
        >
          {new Date(message.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
        </div>

        {/* Action buttons for user messages - always visible */}
        {isUser && !isEditing && (onResend || onEdit) && (
          <div className="flex gap-1 mt-1 justify-end">
            {onEdit && (
              <SimpleTooltip content="Edit message">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-5 w-5 p-0 opacity-50 hover:opacity-100 transition-opacity"
                  onClick={() => setIsEditing(true)}
                >
                  <Pencil className="h-3 w-3" />
                </Button>
              </SimpleTooltip>
            )}
            {onResend && (
              <SimpleTooltip content="Resend message">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-5 w-5 p-0 opacity-50 hover:opacity-100 transition-opacity"
                  onClick={handleResend}
                >
                  <RotateCcw className="h-3 w-3" />
                </Button>
              </SimpleTooltip>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIMessage;
